import { useState } from 'react';
import { useIngredients, Ingredient } from '../../api/queries/inventory.queries';
import { Plus, Search, Beaker, Package, Edit, ChevronDown, ChevronRight } from 'lucide-react';
import IngredientModal from './components/IngredientModal';

type CategoryFilter = 'all' | 'raw' | 'composite';

const INGREDIENT_CATEGORIES = [
  'Tea Base',
  'Milk & Dairy',
  'Sweetener',
  'Topping',
  'Flavor Extract',
  'Syrup',
  'Powder',
  'Other',
];

function IngredientsPage() {
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [expandedIngredients, setExpandedIngredients] = useState<Set<string>>(new Set());
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState<Ingredient | null>(null);

  const { data: ingredients, isLoading, error } = useIngredients();

  const filteredIngredients = ingredients?.filter((ingredient) => {
    const matchesSearch =
      ingredient.name.toLowerCase().includes(search.toLowerCase()) ||
      ingredient.sku.toLowerCase().includes(search.toLowerCase());

    const matchesCategory =
      categoryFilter === 'all' ||
      (categoryFilter === 'composite' && ingredient.isComposite) ||
      (categoryFilter === 'raw' && !ingredient.isComposite);

    return matchesSearch && matchesCategory;
  });

  // Group ingredients by category
  const groupedIngredients = filteredIngredients?.reduce((acc, ingredient) => {
    const category = ingredient.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(ingredient);
    return acc;
  }, {} as Record<string, Ingredient[]>);

  const toggleExpanded = (id: string) => {
    setExpandedIngredients(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const handleEdit = (ingredient: Ingredient) => {
    setEditingIngredient(ingredient);
    setIsModalOpen(true);
  };

  const handleCreate = () => {
    setEditingIngredient(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingIngredient(null);
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          Error loading ingredients: {(error as Error).message}
        </div>
      </div>
    );
  }

  const rawCount = ingredients?.filter(i => !i.isComposite).length || 0;
  const compositeCount = ingredients?.filter(i => i.isComposite).length || 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Ingredients</h1>
          <p className="text-muted-foreground">
            Manage raw materials and composite ingredients (BOM)
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Add Ingredient
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Package className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{rawCount}</p>
              <p className="text-sm text-muted-foreground">Raw Materials</p>
            </div>
          </div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Beaker className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{compositeCount}</p>
              <p className="text-sm text-muted-foreground">Composite (BOM)</p>
            </div>
          </div>
        </div>
        <div className="bg-card border rounded-lg p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{ingredients?.length || 0}</p>
              <p className="text-sm text-muted-foreground">Total Ingredients</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by name or SKU..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              categoryFilter === 'all'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background hover:bg-accent'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setCategoryFilter('raw')}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              categoryFilter === 'raw'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background hover:bg-accent'
            }`}
          >
            Raw Materials
          </button>
          <button
            onClick={() => setCategoryFilter('composite')}
            className={`px-4 py-2 rounded-lg border transition-colors ${
              categoryFilter === 'composite'
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background hover:bg-accent'
            }`}
          >
            Composite (BOM)
          </button>
        </div>
      </div>

      {/* Ingredients List by Category */}
      <div className="space-y-6">
        {groupedIngredients && Object.entries(groupedIngredients).map(([category, items]) => (
          <div key={category} className="bg-card border rounded-lg overflow-hidden">
            <div className="bg-muted/50 px-4 py-3 border-b">
              <h3 className="font-semibold">{category}</h3>
              <p className="text-sm text-muted-foreground">{items.length} ingredients</p>
            </div>
            <div className="divide-y">
              {items.map((ingredient) => (
                <div key={ingredient._id}>
                  <div className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-3">
                      {ingredient.isComposite ? (
                        <button
                          onClick={() => toggleExpanded(ingredient._id)}
                          className="p-1 hover:bg-muted rounded"
                        >
                          {expandedIngredients.has(ingredient._id) ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      ) : (
                        <div className="w-6" />
                      )}
                      <div className={`p-2 rounded-lg ${ingredient.isComposite ? 'bg-purple-100' : 'bg-blue-100'}`}>
                        {ingredient.isComposite ? (
                          <Beaker className="h-4 w-4 text-purple-600" />
                        ) : (
                          <Package className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{ingredient.name}</p>
                        <p className="text-sm text-muted-foreground">
                          SKU: {ingredient.sku} | Unit: {ingredient.unit}
                          {ingredient.isComposite && ingredient.yieldQuantity && (
                            <span className="ml-2 text-purple-600">
                              | Yields: {ingredient.yieldQuantity} {ingredient.yieldUnit}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {ingredient.isComposite && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                          {ingredient.components?.length || 0} components
                        </span>
                      )}
                      <button
                        onClick={() => handleEdit(ingredient)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="Edit ingredient"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded Components */}
                  {ingredient.isComposite && expandedIngredients.has(ingredient._id) && ingredient.components && (
                    <div className="bg-muted/20 px-4 py-3 ml-10 border-t">
                      <p className="text-sm font-medium text-muted-foreground mb-2">Bill of Materials (BOM):</p>
                      <div className="space-y-2">
                        {ingredient.components.map((component, idx) => {
                          const componentIngredient = typeof component.ingredientId === 'object'
                            ? component.ingredientId
                            : ingredients?.find(i => i._id === component.ingredientId);

                          return (
                            <div key={idx} className="flex items-center gap-3 text-sm">
                              <div className="w-2 h-2 bg-purple-400 rounded-full" />
                              <span className="font-medium">
                                {typeof componentIngredient === 'object' && componentIngredient
                                  ? componentIngredient.name
                                  : 'Unknown'}
                              </span>
                              <span className="text-muted-foreground">
                                {component.quantity} {component.unit}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {(!groupedIngredients || Object.keys(groupedIngredients).length === 0) && (
          <div className="text-center py-12 text-muted-foreground">
            <Beaker className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No ingredients found</p>
            <p className="text-sm">Add your first ingredient to get started</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <IngredientModal
          ingredient={editingIngredient}
          ingredients={ingredients || []}
          categories={INGREDIENT_CATEGORIES}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
}

export default IngredientsPage;
